import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ESignatureIntegrationData } from '~/features/admin/ESignature/types';

import { ActiveModal, ESignatureIntegrationState } from './types';

const initialState: ESignatureIntegrationState = {
  activeIntegrationModal: null,
  error: null,
  integrationData: {},
  isCollapsed: true,
  showSidePanel: false,
  loading: false,
  selectedUsers: [],
  connectingVendor: null,
};

const ESignatureSlice = createSlice({
  name: 'esignatureIntegration',
  initialState,
  reducers: {
    clearSelectedUsers(state) {
      state.selectedUsers = [];
    },
    resetIntegrationState(state) {
      Object.assign(state, initialState);
    },
    setActiveModal(
      state,
      action: PayloadAction<Partial<NonNullable<ActiveModal>> | null>,
    ) {
      if (action.payload === null) {
        state.activeIntegrationModal = null;
      } else {
        state.activeIntegrationModal = {
          ...state.activeIntegrationModal,
          ...action.payload,
        } as ActiveModal;
      }
    },
    setIntegrationDraft(
      state,
      action: PayloadAction<{
        id: string;
        draft: ESignatureIntegrationData;
      }>,
    ) {
      const { id, draft } = action.payload;
      state.integrationData[id] = draft;
    },
    setIsCollapsed(state, action: PayloadAction<boolean>) {
      state.isCollapsed = action.payload;
    },
    setSelectedUsers(state, action: PayloadAction<string[]>) {
      state.selectedUsers = action.payload;
    },
    setShowSidePanel(state, action: PayloadAction<boolean>) {
      state.showSidePanel = action.payload;
    },
    toggleUserSelection(state, action: PayloadAction<string>) {
      if (state.selectedUsers.includes(action.payload)) {
        state.selectedUsers = state.selectedUsers.filter(
          (id) => id !== action.payload,
        );
      } else {
        state.selectedUsers.push(action.payload);
      }
    },
    setVendorConnecting(state, action: PayloadAction<string>) {
      state.connectingVendor = action.payload;
    },
    clearVendorConnecting(state) {
      state.connectingVendor = null;
    },
  },
});

export const {
  clearVendorConnecting,
  resetIntegrationState,
  setActiveModal,
  setIntegrationDraft,
  setIsCollapsed,

  setShowSidePanel,
  setVendorConnecting,
} = ESignatureSlice.actions;

export const ESignatureReducer = ESignatureSlice.reducer;

// Selectors
export const selectIsVendorConnecting = (vendor?: string) => {
  return (state: { esignatureIntegration: ESignatureIntegrationState }) => {
    if (!vendor) return false;
    return state.esignatureIntegration.connectingVendor === vendor;
  };
};

export default ESignatureSlice;
